import { NotificationBell } from "@/components/layout/NotificationBell";
import { ScopeToggle } from "@/components/layout/ScopeToggle";
import { Menu, Search, Plus, Sparkles, LogOut, ShieldCheck, Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { QUICK_CREATE } from "@/lib/modules";
import { isHiddenQuick } from "@/lib/workspace";
import { initials } from "@/lib/format";
import { DemoBadge } from "@/components/common/ui-kit";
import { ChangePasswordDialog } from "@/components/layout/ChangePasswordDialog";
import { KeyRound } from "lucide-react";
import { useState } from "react";

const QuickNew = ({ onCreate }) => {
  const { can, me } = useAuth();
  const items = QUICK_CREATE.filter((q) => can(q.perm) && !isHiddenQuick(me?.workspace, q.key));
  if (!items.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="h-9 gap-1.5 rounded-control px-3" data-testid="quick-action-new-dropdown-trigger">
          <Plus className="h-4 w-4" /><span className="hidden sm:inline">Nuovo</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-slate-400">Crea rapidamente</DropdownMenuLabel>
        {items.map(({ key, label, icon: Icon }) => (
          <DropdownMenuItem key={key} onSelect={() => onCreate(key)} data-testid={`quick-action-new-${key}`} className="gap-2.5">
            <Icon className="h-4 w-4 text-slate-500" />{label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const UserMenu = () => {
  const { me, logout, refresh } = useAuth();
  const [pw, setPw] = useState(false);
  const u = me.user;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2.5 rounded-lg p-1 pr-2 transition-colors hover:bg-slate-100" data-testid="topbar-user-profile-button">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-xs font-semibold text-white">{initials(u.full_name)}</span>
          <span className="hidden text-left leading-tight xl:block">
            <span className="block text-sm font-medium text-slate-800">{u.full_name}</span>
            <span className="block text-[11px] text-slate-500" data-testid="topbar-user-role">{me.tenant?.slug ? `${me.tenant.name} · ` : ""}{me.role.name}</span>
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          <p className="text-sm font-medium">{u.full_name}</p>
          <p className="truncate text-xs font-normal text-slate-500" data-testid="user-menu-email">{u.email}</p>
        </DropdownMenuLabel>
        <div className="flex items-center gap-2 px-2 pb-2 text-xs text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5 text-brand-600" />{me.role.name}{me.tenant?.name ? ` · ${me.tenant.name}` : ""}
        </div>
        <DropdownMenuSeparator />
        {me.password_login && <DropdownMenuItem onSelect={() => setPw(true)} className="gap-2" data-testid="user-change-password"><KeyRound className="h-4 w-4" />Cambia password</DropdownMenuItem>}
        <DropdownMenuItem onSelect={logout} className="gap-2 text-red-600 focus:text-red-700" data-testid="user-logout-button">
          <LogOut className="h-4 w-4" />Disconnetti
        </DropdownMenuItem>
      </DropdownMenuContent>
      <ChangePasswordDialog open={pw || !!me.must_change_password} forced={!!me.must_change_password} onOpenChange={setPw} onDone={refresh} />
    </DropdownMenu>
  );
};

const Notifications = () => (
  <Popover>
    <PopoverTrigger asChild>
      <button className="rounded-control p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-ink" aria-label="Notifiche" data-testid="topbar-notifications-button">
        <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
      </button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-72 p-4" data-testid="notifications-popover">
      <p className="text-sm font-semibold text-ink">Notifiche</p>
      <p className="mt-2 text-xs text-slate-500">Nessuna notifica. Le notifiche saranno attivate nella Fase 4.</p>
    </PopoverContent>
  </Popover>
);

export const Topbar = ({ onMenu, onSearch, onAssistant, onCreate }) => {
  const { me, can } = useAuth();
  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-line bg-surface px-4 sm:px-6" data-testid="topbar">
      <button onClick={onMenu} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden" data-testid="topbar-mobile-menu-button" aria-label="Apri menu">
        <Menu className="h-5 w-5" />
      </button>
      {can("search:use") && (
        <button onClick={onSearch} data-testid="global-search-command-palette-trigger"
          className="flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-control border border-line bg-slate-50 px-3 text-sm text-slate-500 transition-colors hover:border-slate-300 hover:bg-white sm:max-w-md">
          <Search className="h-4 w-4 shrink-0" />
          <span className="truncate">Cerca clienti, offerte, interventi…</span>
          <kbd className="ml-auto hidden rounded border border-line bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-500 sm:inline">Ctrl K</kbd>
        </button>
      )}
      <div className="ml-auto flex items-center gap-2">
        {me.data_mode === "demo" && <DemoBadge className="hidden md:inline-flex" data-testid="topbar-demo-badge" />}
        <QuickNew onCreate={onCreate} />
        {can("assistant:use") && (
          <Button variant="outline" onClick={onAssistant} className="h-9 gap-1.5 rounded-control border-line px-3 hover:border-brand-200 hover:bg-brand-50" data-testid="topbar-assistant-toggle-button">
            <Sparkles className="h-4 w-4 text-brand-500" /><span className="hidden md:inline">ON! Assistant</span>
          </Button>
        )}
        <ScopeToggle />
        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  );
};
